import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { wakeBrowserExtension } from '../utils/browserExtensionWake';
import * as humanComputerApi from '../services/humanComputerApi';
import NotificationSoundManager from '../utils/NotificationSoundManager';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';
import { useCurrentChat } from './CurrentChatContext';
import { useMediaViewer } from './MediaViewerContext';

const HumanComputerContext = createContext();

export const useHumanComputer = () => {
  const context = useContext(HumanComputerContext);
  if (!context) {
    throw new Error('useHumanComputer must be used within HumanComputerProvider');
  }
  return context;
};

// Account-lock screens (account_deactivated / email_verification_required) render standalone but this
// provider is mounted ABOVE the router, so its 15s status poll kept firing from a locked session:
// 14,733 rejected GET /human-computer/status from ONE locked account in 4 days (Sep-2 sweep). The axios
// interceptor only redirects when not already on the lock route — so once there, nothing stopped the
// loop. Stop polling on those routes and the moment a lock rejection is seen.
const LOCK_ROUTES = ['/account-deactivated', '/verify-email-required'];
const isLockScreen = () => {
  try { return LOCK_ROUTES.includes(window.location.pathname); } catch (e) { return false; }
};
const isLockRejection = (error) => {
  const t = error?.response?.data?.error_type;
  return error?.response?.status === 403 && (t === 'account_deactivated' || t === 'email_verification_required');
};

export const HumanComputerProvider = ({ children }) => {
  const { currentUser } = useAuth();
  // Defensive consume: when HumanComputerProvider is mounted globally
  // (i.e., above the per-route CurrentChatProvider — which is what we want
  // so /cowork and /cowork/browse get real-time browser status), the
  // CurrentChatContext default is undefined. The HC features that depend
  // on a chat (pending-commands batch, review panel) are no-ops without a
  // current chat anyway, so safe to fall back to undefined here.
  const chatContext = useCurrentChat();
  const currentChat = chatContext?.currentChat;

  // Shared-employee machine status (org sharing): a chat's HC/Browse commands ALWAYS run
  // on the chat OWNER's machine, so when the viewer isn't the owner the toolbar must show
  // the owner's connection state — not the viewer's own (which reads permanently
  // disconnected and hides the owner's Chrome profiles). statusChatId scopes /status and
  // /browser/status to the chat owner's machine; null = self-scoped (owned chats,
  // /cowork pages, no chat).
  const isForeignMachine = !!(
    currentChat?.user?.firebase_uid &&
    currentUser?.uid &&
    currentChat.user.firebase_uid !== currentUser.uid
  );
  const statusChatId = isForeignMachine ? currentChat.id : null;
  const machineOwnerName = isForeignMachine ? currentChat.user?.name || null : null;
  const { openHCReview, closeHCReview, toggleHCReviewFullscreen, rightPanelContent, mode } = useMediaViewer();

  const [state, setState] = useState({
    // Global state
    // CEL-3662 review S1: false until the first /status answer or heartbeat for the current machine scope.
    // Until then isConnected:false means "not known yet", not "not connected": coworkState reads `checking`.
    statusLoaded: false,
    // The machine scope that answer was for (null = the viewer's own machine, else a shared chat id).
    // Loaded = statusLoaded AND statusScope === the scope in view (phase-2 nit N-a): in the render
    // between a scope switch and the wipe below, the old machine's answer never counts as loaded.
    statusScope: null,
    isConnected: false,
    systemInfo: null,
    // Desktop lanes report (agent_docs/desktop_parallel_execution_lanes_prd.md): which command
    // ids a running lane owns and which chat ids wait behind the parallel-chats cap, in queue
    // order. Carried by both status paths (WS heartbeat + /status poll). Empty for old apps.
    lanes: { executing_command_ids: [], queued_chat_ids: [] },
    settings: {
      // Single threshold per surface (terminal + browser). The agent
      // declares `# threat:` on every HC command; this threshold gates
      // auto-approval. See py/cellcog/worker/tool_human_computer.py.
      terminal_threat_threshold: 'safe',
      browser_threat_threshold: 'safe',
    },

    // Per-chat state
    pendingBatch: null,
    currentCommandIndex: 0,

    // Loading states — per-command to avoid all buttons spinning
    statusLoading: false,
    loadingCommandId: null,  // ID of the command currently being approved/denied
    upgradeRequired: false,  // Desktop app needs update
    // CEL-978 single-desktop rule: 2+ desktop apps live on this account —
    // backend holds all command dispatch until one is closed.
    multipleDesktops: false,
    machineHostnames: [],
    // CEL-3646 one active desktop: needsChoice is null until a server that knows the field
    // answers (old server = the CEL-978 hold above, today's copy); true = ambiguous (reason
    // choose | update | clone); false with 2+ desktops = activeHostname runs the commands.
    // machines: [{device_id, hostname, active}] live desktops, sorted by hostname.
    needsChoice: null,
    reason: '',
    activeHostname: '',
    machines: [],
    appVersion: null,        // Current desktop app version (from WebSocket heartbeat)
    minRequiredVersion: null, // Minimum required version (when upgrade needed)
    // CEL-3662: has this account ever had CellCog Desktop connected? connected_before | never | unknown
    // (GET /status `desktop_history`; the server stamps the first check-in). Drives the Cowork card's
    // Download-first vs Open-first copy. 'unknown' until a server that knows the field answers.
    desktopHistory: 'unknown',

    // Browse my Chrome state
    browserStatus: {
      extension_installed: false,
      chrome_running: false,
      available_profiles: [],
      agent_tab_count: 0,
    },
  });

  const statusPollRef = useRef(null);
  const userClosedPanelRef = useRef(false);
  // CEL-3168: the batch id last announced as pending. A NEW id means a new card the owner has not seen.
  const lastPendingBatchIdRef = useRef(null);
  // Ref mirror of statusChatId so the WS subscription (deliberately keyed on [currentUser]
  // only — no resubscribe churn) can read the current machine scope.
  const statusChatIdRef = useRef(null);
  const prevStatusChatIdRef = useRef(statusChatId);
  useEffect(() => {
    statusChatIdRef.current = statusChatId;
    if (prevStatusChatIdRef.current === statusChatId) return;
    prevStatusChatIdRef.current = statusChatId;
    // Machine scope changed (self ↔ shared chat, or between two shared chats): wipe the
    // previous machine's status so it can't render against the new scope while the
    // immediate chat-scoped fetch is in flight (same stale-data class as CEL-975).
    setState(prev => ({
      ...prev,
      statusLoaded: false,
      statusScope: statusChatId,
      isConnected: false,
      systemInfo: null,
      lanes: { executing_command_ids: [], queued_chat_ids: [] },
      upgradeRequired: false,
      multipleDesktops: false,
      machineHostnames: [],
      needsChoice: null,
      reason: '',
      activeHostname: '',
      machines: [],
      desktopHistory: 'unknown',
      browserStatus: {
        extension_installed: false,
        chrome_running: false,
        available_profiles: [],
        agent_tab_count: 0,
      },
    }));
  }, [statusChatId]);
  const prevHCTurnRef = useRef(false);  // Track previous HC turn state
  const lockedRef = useRef(false);  // Set on the first account-lock rejection; silences all further polls
  const wasUpgradeRequiredRef = useRef(false);  // Track if upgrade was needed before disconnect

  // Derived state: desktop app likely restarting to install update
  // True when: was connected + upgrade required, then disconnected
  const mayBeUpdating = !state.isConnected && wasUpgradeRequiredRef.current;

  // Derived state: is the HC review panel currently showing
  const isReviewPanelOpen = rightPanelContent === 'hc_review';

  // Derived state: is the HC review panel in fullscreen mode
  const isHCReviewFullscreen = rightPanelContent === 'hc_review' && mode === 'fullscreen';

  // Viewport-crossing reconciliation (split↔fullscreen at the 1024px edge) moved INTO
  // MediaViewerContext's resize handler (right_panel_coherence_prd.md §3.2) — it now covers
  // hc_review AND media generically, so the HC-local effect that used to live here is gone.

  // ==================== Connection Status (WebSocket + slow fallback) ====================

  // A GET /status body (also what POST active-device returns) → state.
  const applyStatus = useCallback((data, scope = null) => {
    setState(prev => {
      // Track upgrade state transitions for smart disconnection detection
      if (prev.upgradeRequired && prev.isConnected) {
        wasUpgradeRequiredRef.current = true;
      }
      if (data.connected && !data.upgrade_required) {
        wasUpgradeRequiredRef.current = false;
      }
      return {
        ...prev,
        statusLoaded: true,
        statusScope: scope,
        isConnected: data.connected,
        systemInfo: data.system_info,
        lanes: data.lanes || prev.lanes,
        settings: data.settings || prev.settings,
        upgradeRequired: data.upgrade_required || false,
        multipleDesktops: data.multiple_desktops || false,
        machineHostnames: data.machine_hostnames || [],
        // CEL-3646: absent on old servers → null (callers keep the CEL-978 copy).
        needsChoice: typeof data.needs_choice === 'boolean' ? data.needs_choice : null,
        reason: data.reason || '',
        activeHostname: data.active_hostname || '',
        machines: Array.isArray(data.machines) ? data.machines : [],
        // Absent on old servers and on POST /active-device bodies: keep what we know.
        desktopHistory: data.connected
          ? 'connected_before'
          : (typeof data.desktop_history === 'string' ? data.desktop_history : prev.desktopHistory),
      };
    });
  }, []);

  // One-time fetch on mount for bootstrapping, then WebSocket-driven
  const fetchStatus = useCallback(async () => {
    if (!currentUser || lockedRef.current || isLockScreen()) return;
    // Stale-response guard (same class as CEL-975): capture the machine scope at
    // request time; if the user switched scope (self ↔ shared chat) while this
    // request was in flight, drop the response — it describes the wrong machine.
    const requestScope = statusChatId;
    try {
      const { data } = await humanComputerApi.getStatus(statusChatId);
      if (statusChatIdRef.current !== requestScope) return;
      applyStatus(data, requestScope);
      // Also fetch browser status for profiles (best-effort)
      try {
        const { data: browserData } = await humanComputerApi.getBrowserStatus(statusChatId);
        if (statusChatIdRef.current !== requestScope) return;
        setState(prev => ({
          ...prev,
          browserStatus: browserData,
        }));
      } catch { /* best effort — endpoint may not exist on older backends */ }
    } catch (error) {
      if (isLockRejection(error)) {
        // Locked session: the interceptor has (or will) routed to the lock screen. Stop the loop.
        lockedRef.current = true;
        if (statusPollRef.current) { clearInterval(statusPollRef.current); statusPollRef.current = null; }
        return;
      }
      console.error('Failed to fetch HC status:', error);
    }
  }, [currentUser, statusChatId, applyStatus]);

  useEffect(() => {
    if (!currentUser || lockedRef.current || isLockScreen()) return;
    // Bootstrap: one-time fetch on mount
    fetchStatus();
    // Fallback poll (15s) to catch disconnection when Redis key expires.
    // Primary updates come via HUMAN_COMPUTER_CONNECTION_STATUS WebSocket event.
    // 15s (not 60s) ensures quick recovery after sleep/wake when WS messages are missed.
    statusPollRef.current = setInterval(fetchStatus, 15000);
    return () => {
      if (statusPollRef.current) clearInterval(statusPollRef.current);
    };
  }, [currentUser, fetchStatus]);

  // Fetch fresh HC status when frontend WebSocket reconnects or tab becomes visible
  // after being hidden. This closes the gap where HUMAN_COMPUTER_CONNECTION_STATUS
  // messages are lost because the WebSocket wasn't connected when they were published.
  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = UserStreamManager.addReconciliationCallback(() => {
      // Immediate check
      fetchStatus();
      // Delayed re-check: the desktop app may not have polled yet after wake,
      // so Redis still shows "disconnected". Re-check after 5s to catch it.
      setTimeout(fetchStatus, 5000);
    });
    return () => unsubscribe();
  }, [currentUser, fetchStatus]);

  // Subscribe to real-time connection status from Electron's heartbeat.
  // P1-3: The heartbeat carries browser status (browser_connected,
  // browser_extension, browser_tab_count, browser_profiles JSON) inside
  // system_info — extract it into browserStatus so the toolbar's profile
  // dropdown is populated within seconds of WS connect, not 15s later.
  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = UserStreamManager.subscribe('HUMAN_COMPUTER_CONNECTION_STATUS', (data) => {
      // Foreign-machine scope: this WS event describes the VIEWER's OWN desktop heartbeat.
      // When the toolbar is showing the chat owner's machine (shared employee chat),
      // applying it would clobber the chat-scoped status with the wrong machine — drop
      // it; the 15s chat-scoped poll owns updates in that mode.
      if (statusChatIdRef.current) return;
      setState(prev => {
        // Track upgrade state transitions for smart disconnection detection
        if (prev.upgradeRequired && prev.isConnected) {
          wasUpgradeRequiredRef.current = true;
        }
        // Clear the flag when app reconnects without upgrade required (update completed)
        if (data.connected && !data.upgrade_required) {
          wasUpgradeRequiredRef.current = false;
        }

        // Extract browser status from heartbeat. browser_profiles arrives
        // as a JSON string from the Desktop app's poll params.
        let nextBrowserStatus = prev.browserStatus;
        const sys = data.system_info;
        if (sys) {
          let profiles = prev.browserStatus.available_profiles || [];
          const raw = sys.browser_profiles;
          if (typeof raw === 'string' && raw.length > 0) {
            try { profiles = JSON.parse(raw); } catch { /* keep prev */ }
          } else if (Array.isArray(raw)) {
            profiles = raw;
          }
          nextBrowserStatus = {
            extension_installed: !!sys.browser_extension,
            chrome_running: !!sys.browser_connected,
            available_profiles: profiles,
            agent_tab_count: sys.browser_tab_count || 0,
          };
        } else if (!data.connected) {
          // Desktop disconnected → browser is implicitly unreachable too.
          nextBrowserStatus = {
            ...prev.browserStatus,
            chrome_running: false,
            agent_tab_count: 0,
          };
        }

        return {
          ...prev,
          statusLoaded: true,
          statusScope: null, // the heartbeat is the viewer's own machine (foreign scope returned above)
          isConnected: data.connected,
          systemInfo: data.system_info || prev.systemInfo,
          lanes: data.lanes || prev.lanes,
          upgradeRequired: data.upgrade_required !== undefined ? data.upgrade_required : prev.upgradeRequired,
          multipleDesktops: data.multiple_desktops !== undefined ? data.multiple_desktops : prev.multipleDesktops,
          machineHostnames: data.machine_hostnames || prev.machineHostnames,
          needsChoice: typeof data.needs_choice === 'boolean' ? data.needs_choice : prev.needsChoice,
          reason: data.reason !== undefined ? (data.reason || '') : prev.reason,
          activeHostname: data.active_hostname !== undefined ? (data.active_hostname || '') : prev.activeHostname,
          machines: Array.isArray(data.machines) ? data.machines : prev.machines,
          // A live heartbeat IS a desktop check-in (the server stamps it on this same poll).
          desktopHistory: data.connected ? 'connected_before' : prev.desktopHistory,
          appVersion: data.app_version || prev.appVersion,
          minRequiredVersion: data.min_required_version || prev.minRequiredVersion,
          browserStatus: nextBrowserStatus,
        };
      });

      // Push-wake the Chrome extension service worker when Desktop is
      // connected, an extension is installed in at least one profile, but
      // the SW hasn't called back yet (browser_connected=false). This
      // closes the gap between Desktop launching and the SW idling-out-
      // then-waking-on-chrome.alarms (up to 60s in MV3). Wake message is
      // debounced internally so this is safe to call on every heartbeat.
      // See utils/browserExtensionWake.js for guards (no-op when the
      // extension isn't reachable from the current profile).
      const sys = data.system_info;
      if (
        data.connected &&
        !data.upgrade_required &&
        sys &&
        !sys.browser_connected
      ) {
        let profilesForCheck = [];
        const raw = sys.browser_profiles;
        if (typeof raw === 'string' && raw.length > 0) {
          try { profilesForCheck = JSON.parse(raw); } catch { /* ignore */ }
        } else if (Array.isArray(raw)) {
          profilesForCheck = raw;
        }
        if (Array.isArray(profilesForCheck) && profilesForCheck.some(p => p && p.hasExtension)) {
          wakeBrowserExtension('ws-heartbeat');
        }
      }
    });
    return () => unsubscribe();
  }, [currentUser]);

  // ==================== Pending Commands (WebSocket-driven) ====================

  // One-time fetch for bootstrapping when HC turn starts
  const fetchPendingCommands = useCallback(async () => {
    if (!currentChat?.id) return;
    try {
      const { data } = await humanComputerApi.getPendingCommands(currentChat.id);
      if (data.batch) {
        const hasPending = data.batch.commands?.some(c => c.status === 'pending');
        setState(prev => ({
          ...prev,
          pendingBatch: data.batch,
        }));
        // Auto-open panel when there are pending commands (unless user explicitly closed)
        if (hasPending && !userClosedPanelRef.current) {
          openHCReview();
        }
      } else {
        setState(prev => ({
          ...prev,
          pendingBatch: null,
          currentCommandIndex: 0,
        }));
        closeHCReview();
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch pending commands:', error);
      }
    }
  }, [currentChat?.id, openHCReview, closeHCReview]);

  // Reset state when chat changes; fetch once when HC turn starts (no polling)
  useEffect(() => {
    const currentHCTurn = !!currentChat?.human_computer_turn;
    const prevHCTurn = prevHCTurnRef.current;
    prevHCTurnRef.current = currentHCTurn;

    if (!currentChat?.id) {
      setState(prev => ({
        ...prev,
        pendingBatch: null,
        currentCommandIndex: 0,
        loadingCommandId: null,
      }));
      userClosedPanelRef.current = false;
      return;
    }

    if (currentHCTurn) {
      // Only reset userClosedPanel when HC turn transitions from false → true (new batch)
      if (!prevHCTurn) {
        userClosedPanelRef.current = false;
      }
      // One-time fetch for bootstrapping (subsequent updates come via WebSocket)
      fetchPendingCommands();
    } else {
      setState(prev => ({
        ...prev,
        pendingBatch: null,
        currentCommandIndex: 0,
        loadingCommandId: null,
      }));
      // Close the HC review panel if it's open
      if (rightPanelContent === 'hc_review') {
        closeHCReview();
      }
    }
    // No polling interval — WebSocket handles all subsequent updates
  }, [currentChat?.id, currentChat?.human_computer_turn, fetchPendingCommands, closeHCReview, rightPanelContent]);

  // Stale-state recovery: if the chat is in HC turn but our local
  // pendingBatch is empty/null, we missed the WS event or the bootstrap
  // fetch failed. Re-fetch via HTTP after a short delay so the chiclet and
  // review panel can recover without requiring the user to refresh the
  // page. The delay debounces the legitimate "just transitioning into HC
  // turn" window where the fetch is about to fire from the line-280 effect
  // anyway — we only want this safety net to fire if THAT fetch was lost
  // or never ran.
  useEffect(() => {
    if (!currentChat?.id) return;
    if (!currentChat?.human_computer_turn) return;
    const hasBatch = !!(state.pendingBatch?.commands?.length > 0);
    if (hasBatch) return;

    const timer = setTimeout(() => {
      // Re-check inside the timer in case state changed during the wait.
      // Avoids racing with the legitimate bootstrap fetch.
      if (currentChat?.human_computer_turn && !state.pendingBatch?.commands?.length) {
        console.warn('[HC] Recovering: human_computer_turn=true but pendingBatch empty, re-fetching');
        fetchPendingCommands();
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [currentChat?.id, currentChat?.human_computer_turn, state.pendingBatch, fetchPendingCommands]);

  // Listen for HUMAN_COMPUTER_COMMANDS_PENDING WebSocket event (new batch from agent)
  useEffect(() => {
    if (!currentChat?.id) return;
    const unsubscribe = UserStreamManager.subscribe('HUMAN_COMPUTER_COMMANDS_PENDING', (data) => {
      if (data.chat_id !== currentChat?.id) return;
      // Use full batch data from WebSocket if available (avoids HTTP fetch)
      if (data.batch) {
        const hasPending = data.batch.commands?.some(c => c.status === 'pending');

        // Defense-in-depth against the "0 of 0 need approval" chiclet bug:
        // if the batch has nothing pending, this event is informational only
        // (auto-approved commands the agent is about to execute via the
        // backend inline-wait path). Setting `pendingBatch` here would make
        // <HCNotificationChiclet> render an empty "Review pending commands"
        // affordance with no recovery. The backend now also skips publishing
        // for these batches (see _publish_hc_batch_pending callsite in
        // post_office.py), so this branch should rarely fire — but it stays
        // here as a guardrail against future regressions.
        if (!hasPending) return;

        // CEL-3168 (Oct 1 2026): "closed the panel" applied to THAT card. human_computer_turn can stay
        // true across back-to-back batches, so the turn-transition reset never fired and every later
        // card stayed hidden until it timed out at 15 min. A new batch id resets it.
        if (data.batch.batch_id && data.batch.batch_id !== lastPendingBatchIdRef.current) {
          lastPendingBatchIdRef.current = data.batch.batch_id;
          userClosedPanelRef.current = false;
        }
        setState(prev => ({
          ...prev,
          pendingBatch: data.batch,
        }));
        if (!userClosedPanelRef.current) {
          openHCReview();
        }
        // Notify user if they're not viewing this chat
        NotificationSoundManager.play(currentChat.id);
      } else {
        // Fallback: fetch from API if batch not included (backward compatibility)
        fetchPendingCommands();
      }
    });
    return () => unsubscribe();
  }, [currentChat?.id, fetchPendingCommands, openHCReview]);

  // Listen for HUMAN_COMPUTER_BATCH_UPDATED WebSocket event (approve/deny updates)
  useEffect(() => {
    if (!currentChat?.id) return;
    const unsubscribe = UserStreamManager.subscribe('HUMAN_COMPUTER_BATCH_UPDATED', (data) => {
      if (data.chat_id !== currentChat?.id) return;
      if (data.batch) {
        setState(prev => {
          // CEL-3168: updates arrive from several workers (approve route, batch walker, timeout
          // sweep) with no ordering guarantee. Never let an update for ANOTHER batch replace a card
          // that still has commands waiting for the owner.
          const cur = prev.pendingBatch;
          const curWaiting = !!cur?.commands?.some((c) => c.status === 'pending');
          if (curWaiting && cur.batch_id && data.batch.batch_id && cur.batch_id !== data.batch.batch_id) {
            return prev;
          }
          return { ...prev, pendingBatch: data.batch };
        });
      }
    });
    return () => unsubscribe();
  }, [currentChat?.id]);

  // NOTE (V1 scope cut, 2026-06-12): in-chat connection requests were removed
  // entirely. Users connect toolkits on the per-toolkit detail page
  // (/connectors/tools/<toolkit_slug>) and say "Continue" in chat to resume.

  // ==================== Actions ====================

  const approveCommand = useCallback(async (commandId) => {
    if (!currentChat?.id) return;
    setState(prev => ({ ...prev, loadingCommandId: commandId }));
    try {
      await humanComputerApi.approveCommand(currentChat.id, commandId);
      // Optimistic update — WebSocket HUMAN_COMPUTER_BATCH_UPDATED will confirm
      setState(prev => {
        const batch = prev.pendingBatch;
        if (!batch) return { ...prev, loadingCommandId: null };
        const updatedCommands = batch.commands.map(c =>
          c.id === commandId ? { ...c, status: 'approved' } : c
        );
        return {
          ...prev,
          loadingCommandId: null,
          pendingBatch: { ...batch, commands: updatedCommands },
        };
      });
    } catch (error) {
      console.error('Failed to approve command:', error);
      setState(prev => ({ ...prev, loadingCommandId: null }));
    }
  }, [currentChat?.id]);

  const denyCommand = useCallback(async (commandId, reason = null) => {
    if (!currentChat?.id) return;
    setState(prev => ({ ...prev, loadingCommandId: commandId }));
    try {
      await humanComputerApi.denyCommand(currentChat.id, commandId, reason);
      // Optimistic update — WebSocket HUMAN_COMPUTER_BATCH_UPDATED will confirm
      setState(prev => {
        const batch = prev.pendingBatch;
        if (!batch) return { ...prev, loadingCommandId: null };
        const updatedCommands = batch.commands.map(c =>
          c.id === commandId ? { ...c, status: 'denied', denial_reason: reason } : c
        );
        return {
          ...prev,
          loadingCommandId: null,
          pendingBatch: { ...batch, commands: updatedCommands },
        };
      });
    } catch (error) {
      console.error('Failed to deny command:', error);
      setState(prev => ({ ...prev, loadingCommandId: null }));
    }
  }, [currentChat?.id]);

  const approveAll = useCallback(async () => {
    if (!currentChat?.id) return;
    setState(prev => ({ ...prev, loadingCommandId: '__all__' }));
    try {
      await humanComputerApi.approveAllCommands(currentChat.id);
      // Optimistic update — WebSocket HUMAN_COMPUTER_BATCH_UPDATED will confirm
      setState(prev => {
        const batch = prev.pendingBatch;
        if (!batch) return { ...prev, loadingCommandId: null };
        const updatedCommands = batch.commands.map(c =>
          c.status === 'pending' ? { ...c, status: 'approved' } : c
        );
        return {
          ...prev,
          loadingCommandId: null,
          pendingBatch: { ...batch, commands: updatedCommands },
        };
      });
    } catch (error) {
      console.error('Failed to approve all commands:', error);
      setState(prev => ({ ...prev, loadingCommandId: null }));
    }
  }, [currentChat?.id]);

  const denyAll = useCallback(async (reason = null) => {
    if (!currentChat?.id) return;
    setState(prev => ({ ...prev, loadingCommandId: '__all__' }));
    try {
      const batch = state.pendingBatch;
      if (!batch) return;
      const pendingCmds = batch.commands.filter(c => c.status === 'pending');
      for (const cmd of pendingCmds) {
        await humanComputerApi.denyCommand(currentChat.id, cmd.id, reason);
      }
      // Optimistic update — WebSocket HUMAN_COMPUTER_BATCH_UPDATED will confirm
      setState(prev => {
        const batch = prev.pendingBatch;
        if (!batch) return { ...prev, loadingCommandId: null };
        const updatedCommands = batch.commands.map(c =>
          c.status === 'pending' ? { ...c, status: 'denied', denial_reason: reason } : c
        );
        return {
          ...prev,
          loadingCommandId: null,
          pendingBatch: { ...batch, commands: updatedCommands },
        };
      });
    } catch (error) {
      console.error('Failed to deny all commands:', error);
      setState(prev => ({ ...prev, loadingCommandId: null }));
    }
  }, [currentChat?.id, state.pendingBatch]);

  // Terminal threshold update — mirror of the API client's
  // `updateTerminalSettings`. Currently no component consumes this from
  // the context (HumanComputerPage calls the API client directly), but
  // it's kept here so other surfaces can flip the threshold without
  // re-importing humanComputerApi.
  const updateTerminalSettings = useCallback(async (newSettings) => {
    try {
      await humanComputerApi.updateTerminalSettings(newSettings);
      setState(prev => ({
        ...prev,
        settings: { ...prev.settings, ...newSettings },
      }));
    } catch (error) {
      console.error('Failed to update HC terminal settings:', error);
      throw error;
    }
  }, []);

  // CEL-3646: `Use A` — make one desktop the account's active one. The endpoint is current-user
  // only, so a shared chat's viewer (owner's machine in scope) never calls it. Throws to the
  // caller (the surface shows its own error line).
  const chooseDesktop = useCallback(async (deviceId) => {
    if (!deviceId || statusChatIdRef.current) return;
    const { data } = await humanComputerApi.setActiveDevice(deviceId);
    if (statusChatIdRef.current) return;  // scope switched to a shared chat while in flight
    if (data) applyStatus(data);
  }, [applyStatus]);

  const updateChatConfig = useCallback(async (chatId, config) => {
    try {
      await humanComputerApi.updateChatConfig(chatId, config);
    } catch (error) {
      console.error('Failed to update HC chat config:', error);
      throw error;
    }
  }, []);

  const openReviewPanel = useCallback(() => {
    userClosedPanelRef.current = false;
    openHCReview();
  }, [openHCReview]);

  const closeReviewPanel = useCallback(() => {
    userClosedPanelRef.current = true;
    closeHCReview();
  }, [closeHCReview]);

  const value = {
    ...state,
    // N-a: an answer counts only for the machine scope in view right now.
    statusLoaded: state.statusLoaded && state.statusScope === statusChatId,
    isReviewPanelOpen,
    isHCReviewFullscreen,
    mayBeUpdating,
    // Shared-employee machine scope: chat owner's display name when the toolbar is
    // showing ANOTHER user's machine (org-shared chat), null when self-scoped.
    machineOwnerName,
    // True whenever the machine in view is ANOTHER user's (a shared chat's owner), even when that
    // owner has no display name (User.name defaults to ""): read-only surfaces key on this, never on
    // the name (CEL-3662 review P2-S1).
    machineIsOthers: isForeignMachine,
    approveCommand,
    denyCommand,
    approveAll,
    denyAll,
    updateTerminalSettings,
    updateChatConfig,
    chooseDesktop,
    openReviewPanel,
    closeReviewPanel,
    toggleHCReviewFullscreen,
    refreshStatus: fetchStatus,
  };

  return (
    <HumanComputerContext.Provider value={value}>
      {children}
    </HumanComputerContext.Provider>
  );
};

export default HumanComputerContext;
